import type { ReactNode } from 'react';
import type { AssignableApp } from '../api/types.js';

export const TENANT_ADMIN = 'platform:tenantAdmin';

export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <h2 className="sheet__title">{title}</h2>
        {children}
      </div>
    </div>
  );
}

/** Rollen als lesbare Badges, z. B. „Fahrtenbuch: Inhaber/in“. */
export function RoleBadges({ roles, apps }: { roles: string[]; apps: AssignableApp[] }) {
  if (roles.length === 0) return <span className="muted" style={{ fontSize: 13 }}>Keine Rollen</span>;
  return (
    <span className="badges">
      {roles.map((r) => (
        <span key={r} className={`badge ${r === TENANT_ADMIN ? 'badge--admin' : 'badge--info'}`}>
          {roleLabel(r, apps)}
        </span>
      ))}
    </span>
  );
}

export function roleLabel(role: string, apps: AssignableApp[]): string {
  if (role === TENANT_ADMIN) return 'Admin des Betriebs';
  const [appId, roleId] = role.split(':');
  const app = apps.find((a) => a.appId === appId);
  const label = app?.roles.find((r) => r.id === roleId)?.label ?? roleId;
  return `${app?.name ?? appId}: ${label}`;
}

/**
 * Auswahl der Rollen je freigeschaltetem Tool. „Admin des Betriebs“ nur mit `withAdmin` – die Rolle
 * vergibt allein die Betriebsam-Administration; ohne bleibt eine vorhandene Admin-Rolle unverändert im Wert.
 */
export function RoleEditor({
  apps,
  value,
  onChange,
  idPrefix,
  withAdmin = false,
}: {
  apps: AssignableApp[];
  value: string[];
  onChange: (roles: string[]) => void;
  idPrefix: string;
  withAdmin?: boolean;
}) {
  const toggle = (role: string) => onChange(value.includes(role) ? value.filter((r) => r !== role) : [...value, role]);
  return (
    <fieldset className="role-editor">
      <legend className="field__label">Rollen</legend>
      {withAdmin && (
        <label className="check">
          <input type="checkbox" checked={value.includes(TENANT_ADMIN)} onChange={() => toggle(TENANT_ADMIN)} id={`${idPrefix}-admin`} />
          <span>
            <strong>Admin des Betriebs</strong>
            <span className="muted">Team verwalten, Tool-Rollen vergeben</span>
          </span>
        </label>
      )}
      {apps.map((app) => (
        <div key={app.appId} className="role-editor__app">
          <span className="role-editor__app-name">{app.name}</span>
          {app.roles.map((r) => {
            const role = `${app.appId}:${r.id}`;
            return (
              <label key={role} className="check">
                <input type="checkbox" checked={value.includes(role)} onChange={() => toggle(role)} />
                <span>
                  <strong>{r.label}</strong>
                  {r.description && <span className="muted">{r.description}</span>}
                </span>
              </label>
            );
          })}
        </div>
      ))}
      {apps.length === 0 && <p className="note">Für diesen Betrieb sind noch keine Tools freigeschaltet.</p>}
    </fieldset>
  );
}

export const STATUS_LABEL: Record<string, { label: string; tone: string }> = {
  active: { label: 'aktiv', tone: 'ok' },
  invited: { label: 'eingeladen', tone: 'pending' },
  suspended: { label: 'gesperrt', tone: 'warn' },
  deleting: { label: 'wird gelöscht', tone: 'warn' },
  deleted: { label: 'gelöscht', tone: 'neutral' },
  pending: { label: 'Antwort ausstehend', tone: 'pending' },
  retentionLocked: { label: 'gesperrt (Aufbewahrung)', tone: 'neutral' },
  purged: { label: 'gelöscht', tone: 'ok' },
};

export function StatusBadge({ status }: { status: string }) {
  const s = STATUS_LABEL[status] ?? { label: status, tone: 'neutral' };
  return <span className={`badge badge--${s.tone}`}>{s.label}</span>;
}

export function dateDe(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });
}
